package screens.admin

import androidx.compose.runtime.*
import com.example.readingnotes.di.AppModule
import com.example.readingnotes.domain.usecase.DeletePostUseCase
import com.example.readingnotes.domain.usecase.UpdatePostUseCase
import com.example.readingnotes.presentation.admin.AdminPostEditorEvent
import com.example.readingnotes.presentation.admin.AdminPostEditorViewModel
import components.StatusSelector
import kotlinx.browser.document
import kotlinx.coroutines.CoroutineScope
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import styles.AppStyleSheet
import utils.handleImageUpload

@Composable
fun AdminPostEditorScreen(
    postId: Int? = null,
    onPostSuccess: () -> Unit,
    onBackClick: () -> Unit,
    onTotpSetupClick: () -> Unit = {},
    onLogoutClick: () -> Unit = {},
    showTotpSetup: Boolean = false,
    appModule: AppModule,
    appScope: CoroutineScope,
) {
    val viewModel =
        remember(postId) {
            AdminPostEditorViewModel(
                createPostUseCase = appModule.createPostUseCase,
                viewModelScope = appScope,
                postRepository = appModule.postRepository,
                updatePostUseCase = UpdatePostUseCase(appModule.postRepository),
                deletePostUseCase = DeletePostUseCase(appModule.postRepository),
                uploadImageUseCase = appModule.uploadImageUseCase,
            )
        }
    val state by viewModel.state.collectAsState()
    var showDeleteConfirm by remember { mutableStateOf(false) }

    // Load post for edit mode
    LaunchedEffect(postId) {
        if (postId != null) {
            viewModel.onEvent(AdminPostEditorEvent.OnLoadPost(postId))
        }
    }

    // Navigate to post list on successful post creation/update
    if (state.isSuccess || state.deleteSuccess) {
        LaunchedEffect(Unit) {
            onPostSuccess()
        }
    }

    // Delete confirmation dialog
    if (showDeleteConfirm) {
        Div({ classes(AppStyleSheet.modalOverlay) }) {
            Div({ classes(AppStyleSheet.modalContent) }) {
                H3 { Text("投稿を削除") }
                P { Text("この投稿を削除してもよろしいですか？この操作は取り消せません。") }
                Div({ classes(AppStyleSheet.modalButtons) }) {
                    Button({
                        classes(AppStyleSheet.cancelButton)
                        onClick { showDeleteConfirm = false }
                    }) { Text("キャンセル") }
                    Button({
                        classes(AppStyleSheet.deleteButton)
                        onClick {
                            postId?.let {
                                viewModel.onEvent(AdminPostEditorEvent.OnDeletePost(it))
                            }
                            viewModel.onEvent(AdminPostEditorEvent.OnConfirmDelete)
                            showDeleteConfirm = false
                        }
                        if (state.isDeleting) {
                            attr("disabled", "true")
                        }
                    }) {
                        Text(if (state.isDeleting) "削除中..." else "削除する")
                    }
                }
            }
        }
    }

    Div({ classes(AppStyleSheet.editorContainer) }) {
        // Header with back button and logout button
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
            }
        }) {
            Button({
                classes(AppStyleSheet.backButton)
                onClick { onBackClick() }
            }) { Text("← 戻る") }

            Button({
                classes(AppStyleSheet.logoutButton)
                onClick { onLogoutClick() }
            }) { Text("ログアウト") }
        }

        Div({ classes(AppStyleSheet.editorForm) }) {
            H2({ classes(AppStyleSheet.editorTitle) }) {
                Text(if (state.isEditMode) "投稿を編集" else "新規投稿")
            }

            // Loading state for edit mode
            if (state.isLoading) {
                Div({ classes(AppStyleSheet.loading) }) { Text("読み込み中...") }
            } else {
                // TOTP setup prompt
                if (showTotpSetup && !state.isEditMode) {
                    Div({ classes(AppStyleSheet.totpSetupPrompt) }) {
                        Text("🔐 二要素認証を設定してセキュリティを強化しましょう ")
                        A(attrs = {
                            classes(AppStyleSheet.linkStyle)
                            onClick { onTotpSetupClick() }
                        }) { Text("設定する") }
                    }
                }

                // Title input field
                Div({ classes(AppStyleSheet.formGroup) }) {
                    Label { Text("タイトル") }
                    Input(type = InputType.Text) {
                        classes(AppStyleSheet.textInput)
                        value(state.titleInput)
                        onInput { viewModel.onEvent(AdminPostEditorEvent.OnTitleChanged(it.value)) }
                        if (state.isSubmitting) {
                            attr("disabled", "true")
                        }
                    }
                }

                // Content input field (textarea)
                Div({ classes(AppStyleSheet.formGroup) }) {
                    Label { Text("本文") }
                    TextArea(attrs = {
                        classes(AppStyleSheet.editorTextArea)
                        onInput { viewModel.onEvent(AdminPostEditorEvent.OnContentChanged(it.value)) }
                        if (state.isSubmitting) {
                            attr("disabled", "true")
                        }
                    }, value = state.contentInput)
                }

                // Image upload section (R2 storage)
                Div({ classes(AppStyleSheet.formGroup) }) {
                    Label { Text("画像アップロード") }

                    // Setup event listener
                    LaunchedEffect(Unit) {
                        val element = document.getElementById("image-upload-input")
                        if (element != null) {
                            val listener = { event: dynamic ->
                                val files = event.target.files
                                handleImageUpload(files, viewModel)
                            }
                            element.addEventListener("change", listener)
                        }
                    }

                    // File input (hidden)
                    Input(type = InputType.File) {
                        attr("id", "image-upload-input")
                        attr("accept", "image/png,image/jpeg,image/jpg,image/webp,image/gif")
                        attr("multiple", "false")
                        style {
                            property("display", "none")
                        }
                    }

                    // Upload button
                    Button({
                        classes(AppStyleSheet.imageUploadButton)
                        onClick {
                            document.getElementById("image-upload-input")?.asDynamic()?.click()
                        }
                        if (state.isUploadingImage) {
                            attr("disabled", "true")
                        }
                    }) {
                        Text(if (state.isUploadingImage) "アップロード中..." else "画像を選択")
                    }

                    // Upload status
                    if (state.isUploadingImage) {
                        Div({ classes(AppStyleSheet.uploadStatus) }) {
                            Text("画像をアップロードしています...")
                        }
                    }

                    // Upload error
                    if (state.imageUploadError != null) {
                        Div({ classes(AppStyleSheet.uploadError) }) {
                            Text(state.imageUploadError!!)
                        }
                    }

                    // Image preview
                    if (state.tempImageUrl != null) {
                        Div({ classes(AppStyleSheet.imagePreview) }) {
                            Text("プレビュー:")
                            Img(src = state.tempImageUrl!!) {
                                classes(AppStyleSheet.imagePreviewImg)
                                attr("alt", "プレビュー画像")
                            }
                        }
                    }

                    // Uploaded images
                    if (state.images.isNotEmpty()) {
                        Div({ classes(AppStyleSheet.imagePreview) }) {
                            Text("登録済み画像 (${state.images.size}枚)")
                            state.images.forEach { image ->
                                Div({ classes(AppStyleSheet.uploadedImageItem) }) {
                                    Span({ classes(AppStyleSheet.uploadedImageName) }) { Text(image.originalName) }
                                    Div({ classes(AppStyleSheet.imageActionButtons) }) {
                                        Button({
                                            classes(AppStyleSheet.imageActionButton)
                                            onClick {
                                                // Insert markdown image link into content
                                                val markdownLink = "![${image.originalName}](${image.url})"
                                                viewModel.onEvent(AdminPostEditorEvent.OnContentChanged(
                                                    state.contentInput + "\n\n" + markdownLink
                                                ))
                                            }
                                        }) { Text("本文に挿入") }
                                        Button({
                                            classes(AppStyleSheet.imageDeleteButton)
                                            onClick { viewModel.onEvent(AdminPostEditorEvent.OnRemoveImage(image.id)) }
                                        }) { Text("削除") }
                                    }
                                }
                            }
                        }
                    }
                }

                // Status selector (edit mode only)
                if (state.isEditMode) {
                    StatusSelector(
                        currentStatus = state.statusInput,
                        onStatusChange = { status ->
                            viewModel.onEvent(AdminPostEditorEvent.OnStatusChanged(status))
                        },
                    )
                }

                // Error message display
                if (state.errorMessage != null) {
                    Div({ classes(AppStyleSheet.formError) }) { Text(state.errorMessage!!) }
                }

                // Button group
                Div({ classes(AppStyleSheet.editorButtonGroup) }) {
                    // Delete button (edit mode only)
                    if (state.isEditMode && postId != null) {
                        Button({
                            classes(AppStyleSheet.deleteButton)
                            onClick { showDeleteConfirm = true }
                            if (state.isSubmitting || state.isDeleting) {
                                attr("disabled", "true")
                            }
                        }) { Text("削除") }
                    }

                    // Submit button
                    Button({
                        classes(AppStyleSheet.submitButton)
                        onClick {
                            if (state.isEditMode && postId != null) {
                                viewModel.onEvent(AdminPostEditorEvent.OnUpdatePost(postId))
                            } else {
                                viewModel.onEvent(AdminPostEditorEvent.OnSubmit)
                            }
                        }
                        if (state.isSubmitting) {
                            attr("disabled", "true")
                        }
                    }) {
                        Text(
                            when {
                                state.isSubmitting && state.isEditMode -> "更新中..."
                                state.isSubmitting -> "投稿中..."
                                state.isEditMode -> "更新する"
                                else -> "投稿する"
                            },
                        )
                    }
                }
            }
        }
    }
}
